import { useRef } from 'react';

import { isEqual } from '@/utils/lodash.util';

/**
 * Returns a stable reference to the value, only updating when
 * the value has actually changed (deep equality comparison).
 *
 * This prevents useMemo/useEffect dependencies from triggering
 * unnecessarily when object contents haven't changed.
 *
 * WHEN TO USE:
 * - Object values in useMemo dependency arrays
 * - Filter objects that may have same content but different references
 *
 * WHEN NOT TO USE:
 * - TanStack Query queryKeys (JSON.stringify is acceptable there)
 * - Primitive values (already stable)
 * - Already memoized values
 *
 * @example
 * const stableFilters = useStableValue(filters.advancedFilters);
 * const query = useMemo(() => buildQuery(stableFilters), [stableFilters]);
 */
export function useStableValue<T>(value: T): T {
  const ref = useRef<T>(value);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}

/**
 * Stabilize multiple properties from an object at once.
 * Returns an object with the same keys but stable references.
 *
 * @example
 * const { advancedFilters, sort } = useStableProperties(filters, ['advancedFilters', 'sort']);
 */
export function useStableProperties<
  T extends Record<string, unknown>,
  K extends keyof T,
>(obj: T, keys: K[]): Pick<T, K> {
  const ref = useRef<Pick<T, K>>({} as Pick<T, K>);

  let hasChanged = false;
  const result = {} as Pick<T, K>;

  for (const key of keys) {
    if (!isEqual(ref.current[key], obj[key])) {
      hasChanged = true;
    }
    result[key] = obj[key];
  }

  if (hasChanged) {
    ref.current = result;
  }

  return ref.current;
}
